import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useCallback, useEffect } from 'react';
import { defaultDocumentName, saveAsTemplateMenuId } from './richedit-helper';
import {
  DXR,
  RichEdit,
  CustomCommandExecutedEventArgs,
} from '../../../common/devexpress/RichEdit/dx-richedit';
import { unfocusRichEditor } from './unfocusRichEditor';
import { useModal } from '../../common/ui-kit/modal/useModal';

type Props = {
  richEdit: React.MutableRefObject<RichEdit>;
};

/*
  Hook that adds and handles "Save as" menu item
 */
export const SaveTemplateAsRichEditButton: React.FC<Props> = (props) => {
  const { richEdit } = props;
  const modals = useModal();
  const i18n = useScopedTranslation('documents');

  const saveAsCustomCommandExecutedEventHandler = useCallback(
    async (
      sender: RichEdit | undefined,
      args: CustomCommandExecutedEventArgs | undefined,
    ) => {
      if (!sender || !args || args.commandName !== saveAsTemplateMenuId) {
        return;
      }
      await unfocusRichEditor();
      const newName = await modals.showPrompt({
        text: '',
        fieldName: i18n.t('save_template_as_name_prompt.text'),
        isMandatory: true,
        maxLength: 200,
        defaultValue:
          sender.fileName === defaultDocumentName ? '' : sender.fileName,
        title: i18n.t('save_template_as_name_prompt.title'),
      });

      if (newName) {
        // setting hasUnsavedChanges to true is required, otherwise there's a following issue:
        // 1. Create new template
        // 2. Type some symbols
        // 3. Click Save (not Save As). Enter some name.
        // 4. Template is NOT actually saved.
        sender.hasUnsavedChanges = true;
        sender.saveDocument(
          DXR.DocumentFormat.OpenXml,
          undefined,
          newName.trim(),
        );

        setTimeout(() => {
          // this fixes #46557: "Save" button is active after clicking "Save as" in Document templates
          sender.hasUnsavedChanges = false;
        }, 1);
      }
    },
    [],
  );

  const documentLoadedHandler = useCallback(() => {
    // fix #46557: "Save" button is active after clicking "Save as" in Document templates
    richEdit.current.hasUnsavedChanges = false;
  }, []);

  // adds SaveAs menu item
  useEffect(() => {
    // add button if it doesn't exist
    richEdit.current.updateRibbon((ribbon) => {
      if (!ribbon) return;
      const filePanel = ribbon.getTab(DXR.RibbonTabType.File)!;
      const saveAsButton = filePanel.getItem(saveAsTemplateMenuId);
      if (!saveAsButton) {
        filePanel.insertItem(
          new DXR.RibbonButtonItem(
            saveAsTemplateMenuId,
            i18n.t('template_save_as_button'),
            {
              icon: 'dxre-icon-Save',
              showText: true,
            },
          ),
          3,
        );
      }
    });

    richEdit.current.events.customCommandExecuted.addHandler(
      saveAsCustomCommandExecutedEventHandler,
    );
    richEdit.current.events.documentLoaded.addHandler(documentLoadedHandler);
    return () => {
      richEdit.current.events.customCommandExecuted.removeHandler(
        saveAsCustomCommandExecutedEventHandler,
      );
      richEdit.current.events.documentLoaded.removeHandler(
        documentLoadedHandler,
      );
    };
  }, [saveAsCustomCommandExecutedEventHandler]);

  return null;
};
